{
  "label": "Admin",
  "nav": {
    "dashboard": "Dashboard",
    "newAssignment": "New assignment",
    "instances": "All Brev instances",
    "settings": "Settings",
    "devTools": "Dev tools",
    "devStates": "Candidate states",
    "devAdminPreview": "Admin preview",
    "devEmails": "Emails",
    "signedInAs": "Signed in as",
    "openMenu": "Open navigation",
    "closeMenu": "Close navigation",
    "build": "Build",
    "timezone": "Time zone",
    "developmentBuild": "development",
    "devOverview": "Feature previews"
  },
  "role": {
    "owner": "Owner",
    "admin": "Admin"
  },
  "gate": {
    "signInTitle": "Sign in through Cloudflare Access",
    "signInBody": "The admin area is protected by Cloudflare Access with your Google Workspace account.",
    "reload": "Sign in",
    "notAdminTitle": "This account is not an admin",
    "notAdminBody": "Ask the owner to add you in Settings → Admins.",
    "errorTitle": "Could not load the admin area"
  },
  "banner": {
    "brevCredential": "Brev login required — the stored NVIDIA session no longer refreshes. Follow the runbook step “Rotate Brev credential”.",
    "unmanaged": "{{count}} running Brev instance is not managed by the assessment portal. Check “All Brev instances”.",
    "unmanaged_other": "{{count}} running Brev instances are not managed by the assessment portal. Check “All Brev instances”."
  },
  "status": {
    "draft": "Draft",
    "link_issued": "Link issued",
    "active": "Active",
    "finished": "Finished",
    "expired": "Expired",
    "revoked": "Revoked",
    "archived": "Archived",
    "not_started": "Not started"
  },
  "endReason": {
    "finished": "finished by candidate",
    "budget_exhausted": "GPU budget used up",
    "window_expired": "Time limit reached",
    "revoked": "revoked by admin",
    "session_expired": "Continuous session ended",
    "deadline_expired": "Fixed deadline reached"
  },
  "dashboard": {
    "title": "Dashboard",
    "tiles": {
      "runningNow": "Running now",
      "active": "Active assignments",
      "gpuHours": "GPU hours this month",
      "spend": "Estimated spend this month",
      "spendOf": "soft budget {{budget}} per candidate",
      "failures": "Failures"
    },
    "table": {
      "candidate": "Candidate",
      "email": "Email",
      "state": "State",
      "gpu": "GPU time",
      "windowEnd": "Window ends",
      "spend": "Spend",
      "lastActivity": "Last activity",
      "noInstance": "No instance",
      "notLaunched": "Not launched yet"
    },
    "filters": {
      "all": "All"
    },
    "search": "Search name, email, title",
    "empty": "No assignments yet.",
    "emptyHint": "Create the first assignment to prepare an instance for a candidate.",
    "maxReached": "Two assignments are already active. Finish or revoke one first, or raise the limit in Settings.",
    "refreshed": "Updated <timestamp/>"
  },
  "wizard": {
    "title": "New assignment",
    "steps": {
      "candidate": "Candidate",
      "brief": "Brief",
      "limits": "Limits",
      "gpu": "GPU",
      "review": "Review",
      "kind": "Type",
      "ai": "AI assistance"
    },
    "stepOf": "Step {{n}} of {{total}}",
    "candidate": {
      "email": "Candidate email",
      "emailHelp": "The sign-in code goes to this address. HR sends the invite link separately.",
      "name": "Candidate name",
      "title": "Assignment title",
      "titleHelp": "Shown to the candidate in the header, e.g. “Isaac Sim take-home”.",
      "notes": "Internal notes",
      "notesHelp": "Admin only. Never shown to the candidate."
    },
    "brief": {
      "url": "Assignment brief URL (Notion)",
      "help": "The candidate sees this as an “Assignment brief” button. Leave empty if the brief is sent by HR.",
      "none": "No brief link"
    },
    "limits": {
      "budgetHours": "GPU budget (hours)",
      "budgetHelp": "= {{minutes}} minutes. Counted only while the GPU is running.",
      "windowDays": "Window (days from first Launch)",
      "windowHelp": "After this the link and the session end regardless of hours left.",
      "inviteDays": "Invite link valid (days)",
      "inviteHelp": "Counted from the moment you copy the link for HR. Restarts when you regenerate the link.",
      "sessionMinutes": "Continuous session (minutes, optional)",
      "sessionHelp": "Starts at the first Launch of this attempt and keeps counting while paused. Leave blank to disable.",
      "deadlineAt": "Fixed deadline (optional)",
      "deadlineHelp": "Using {{timezone}} timezone. This deadline also applies before the assessment starts. Leave blank to disable.",
      "earliest": "Access ends when the assessment window or an enabled cutoff is reached. Pausing stops GPU usage only.",
      "none": "Not enabled",
      "preview": "If launched now, access would end at <timestamp/>.",
      "invitationGroup": "Before the assessment",
      "workGroup": "Working time",
      "optionalGroup": "Optional cutoffs",
      "writtenEarliest": "Access ends when the assessment window or an enabled cutoff is reached. Leaving the page does not pause the clock.",
      "writtenPreview": "If opened now, access would end at <timestamp/>."
    },
    "gpu": {
      "type": "GPU",
      "perHour": "US${{rate}}/h",
      "l40sDesc": "Default. 48 GB VRAM, RT cores. AWS g6e.4xlarge.",
      "a10gDesc": "Cheaper. 24 GB VRAM, RT cores. Tighter for large scenes.",
      "disk": "Disk (GB)",
      "diskHelp": "Kept while paused; storage is billed while the instance exists.",
      "region": "Host region",
      "regionHelp": "Any region lets Brev choose a region and zone with free GPUs, like Brev launchables do. Choose a specific AWS region only when you need one; GPU availability depends on the region.",
      "anyRegion": "Any region (Brev picks one with capacity)",
      "machine": "Machine: {{machine}}",
      "pricingCatalog": "Brev catalog estimate for this machine and region. Any-region prices may vary with placement; storage is billed separately. Actual cost comes from Brev metered usage.",
      "pricingFallback": "A current Brev quote is unavailable for this machine and region. Showing the configured fallback estimate; this does not confirm machine availability."
    },
    "review": {
      "title": "Review",
      "hint": "Create instance will call Brev and create the GPU workspace (billing starts when it is started).",
      "estimate": "GPU estimate for this budget: about US${{usd}}"
    },
    "create": "Create instance",
    "creating": "Creating…",
    "created": "Instance created. Follow the checklist to prepare it.",
    "next": "Next",
    "back": "Back",
    "errors": {
      "max_active_reached": "Two assignments are already active. Finish or revoke one first, or raise the limit in Settings.",
      "candidate_exists": "A candidate with this email already has an assignment.",
      "brev_error": "Brev refused the request. See the error details and try again.",
      "continuous_session_disabled": "Continuous sessions were disabled in admin settings. Reload the wizard to use the current settings."
    },
    "kind": "Assignment type",
    "kinds": {
      "isaac_sim": {
        "title": "Isaac Sim take-home",
        "description": "GPU workspace with VS Code and the Isaac Sim viewer."
      },
      "gpu_vscode": {
        "title": "GPU coding take-home",
        "description": "GPU workspace with VS Code for coding. No simulation viewer."
      },
      "free_response": {
        "title": "Written take-home assessment",
        "description": "Read a Markdown question and submit a written answer in the browser. No GPU."
      }
    }
  },
  "detail": {
    "title": "Assignment",
    "backToDashboard": "Dashboard",
    "endedReason": "Ended: {{reason}}",
    "checklist": {
      "title": "Preparation checklist",
      "hint": "Preparation runs automatically. Share the invitation with HR; candidates can launch once Isaac Sim is ready.",
      "created": "Instance created",
      "started": "GPU started automatically",
      "startedHint": "The GPU starts after creation and prepares the workspace automatically.",
      "warmup": "Automatic warm-up complete",
      "warmupHint": "Isaac Sim starts and builds its shader cache automatically. Readiness is verified when the streaming app finishes loading. Preheat completion notifies admins only.",
      "autoPause": "Automatic pause or candidate launch",
      "stopHint": "The GPU pauses automatically after the workspace stays ready for 3 minutes, unless the candidate launches first. Launch resumes a paused workspace; preparation uses no candidate GPU time.",
      "linkIssued": "Invitation link generated",
      "linkIssuedHint": "Generated when the assignment is created and always available above. Candidates can launch after warm-up.",
      "markWarmup": "Mark warm-up verified",
      "copyLink": "Generate invitation link",
      "regenerate": "Regenerate link",
      "issuedBy": "issued by {{by}} · <timestamp/>",
      "linkExpires": "Link valid until <timestamp/>",
      "doneBy": "{{by}} · <timestamp/>",
      "gpuHint": "Preparation runs automatically. Candidates can launch when the workspace is ready.",
      "workspace": "Workspace verified",
      "workspaceHint": "VS Code readiness is checked automatically. Open VS Code to verify the workspace.",
      "complete": "Preparation complete"
    },
    "inviteDialog": {
      "title": "Invite link for HR",
      "body": "This link stays available here. Copy it or the message below for HR. Candidates verify their email before entering; Launch becomes available after warm-up.",
      "link": "Invite link",
      "text": "Text for HR to paste",
      "copyText": "Copy text",
      "legacy": "This older link was stored as a hash only. It still works, but cannot be recovered. Use Regenerate link below once to replace it with a link that stays visible here.",
      "message": {
        "intro": "Hello, here is your sign-in link for the {{kind}} assignment: {{url}}",
        "linkExpiry": "· The invitation is valid for {{days}} days, until {{expiry}}.",
        "writtenWindow": "· Your {{days}}-day assignment window starts when you first open the question.",
        "gpuWindow": "· Your {{days}}-day assignment window starts at your first Launch.",
        "activeWindow": "· Deadline: {{deadline}}. Your assignment window has already started; returning does not reset it.",
        "writtenInstructions": "· Read the question, write your Markdown answer, and submit in the browser.",
        "gpuBudget": "· You have {{hours}} GPU hours in total, counted only while the machine runs. Pause when you are not using it.",
        "gpuRemaining": "· GPU time remaining: {{remaining}} (HH:MM:SS, as of {{asOf}}). Check the workspace for the latest balance; pause when not using it.",
        "sessionLimit": "· Continuous session limit: {{minutes}} minutes from the start of this attempt; the clock continues while you are away.",
        "fixedDeadline": "· Fixed deadline: {{deadline}}.",
        "writtenCutoff": "· The earliest configured window, continuous-session, or fixed deadline ends editing.",
        "gpuCutoff": "· Access ends at the earliest configured window, continuous-session, or fixed deadline, or when the GPU budget runs out.",
        "reply": "Reply to this email with any questions."
      },
      "writtenBody": "This link stays available here. Copy it or the bilingual message below for HR. Candidates verify their email and consent before opening the question."
    },
    "regenerateDialog": {
      "title": "Regenerate the invite link?",
      "body": "The old link stops working immediately. The validity period restarts. Give HR the new link."
    },
    "countdowns": {
      "title": "What the candidate sees",
      "gpuLeft": "GPU time left",
      "deadline": "Deadline",
      "deadlineNotStarted": "{{days}} days from first Launch",
      "grace": "Grace ends",
      "used": "Used {{used}} of {{budget}}",
      "gpuPaused": "pause"
    },
    "instance": {
      "pendingCreation": "Workspace creation is still being prepared or confirmed. If setup failed, resolve the error and retry creation; the assessment portal checks for an existing machine before creating another.",
      "region": "Region",
      "title": "Instance",
      "none": "No instance attached.",
      "brevId": "Brev workspace",
      "name": "Name",
      "type": "GPU",
      "ip": "Public IP",
      "dns": "DNS",
      "brevStatus": "Brev status",
      "health": "Health",
      "lastSynced": "Last synced",
      "lastError": "Last error",
      "vscodePassword": "VS Code password",
      "viewerAuth": "Viewer login",
      "openVscode": "Open VS Code",
      "openViewer": "Open viewer",
      "noIp": "No public IP yet",
      "build": "Build",
      "createdAt": "Created <timestamp/>"
    },
    "buildProgress": {
      "vmBuild": "VM build",
      "script": "Setup script",
      "recovered": "Previous setup failure; readiness subsequently verified.",
      "history": "Previous setup attempt",
      "attemptEnded": "Attempt ended"
    },
    "actions": {
      "title": "Actions",
      "start": "Start",
      "stop": "Pause",
      "reset": "Reset",
      "delete": "Delete instance",
      "extendBudget": "Extend budget",
      "extendWindow": "Extend window",
      "revoke": "Revoke",
      "markFinished": "Mark finished",
      "retryCreate": "Retry create",
      "working": "Working…",
      "reopen": "Reopen assignment",
      "changeRegion": "Change region",
      "editLimits": "Edit time limits"
    },
    "dialogs": {
      "stopTitle": "Pause the instance?",
      "stopBody": "The GPU stops and billing drops to storage only. Files in the workspace are kept.",
      "resetTitle": "Reset the instance?",
      "resetBody": "Brev reboots the machine. Use this when the instance is unhealthy.",
      "deleteTitle": "Delete the instance",
      "deleteBody": "This deletes the Brev workspace and everything on it. The candidate's work is lost. Type the candidate email shown below to confirm.",
      "deleteConfirmLabel": "Candidate email",
      "deleteEmailUnavailable": "Candidate email unavailable. Deletion cannot be confirmed.",
      "extendBudgetTitle": "Extend GPU budget",
      "extendBudgetBody": "Adds time to the candidate’s budget and cancels an active grace period. Candidates who have signed in receive an email if enabled in Settings.",
      "extendBudgetField": "Add hours",
      "extendWindowTitle": "Extend window",
      "extendWindowBody": "Adds days to the work window and reactivates the link if it had expired. Candidates who have signed in receive an email if enabled in Settings.",
      "extendWindowField": "Add days",
      "revokeTitle": "Revoke this assignment?",
      "revokeBody": "The instance stops, all sessions end and the invite link dies. The candidate cannot sign in again.",
      "finishTitle": "Mark as finished?",
      "finishBody": "Same as the candidate pressing Finish: the GPU stops and the assignment locks.",
      "startBody": "Start the GPU. Billing runs while it is running.",
      "changeRegionTitle": "Create in another region",
      "changeRegionBody": "Deletes the workspace that failed to start and creates a new one in the selected region. The invite link, GPU budget and window stay the same. Available only before the machine has ever run.",
      "changeRegionField": "New region",
      "changeRegionCurrent": "Current region: {{region}}",
      "changeRegionSuccess": "Creating the workspace in {{region}}"
    },
    "tabs": {
      "audit": "Audit log",
      "segments": "Run segments",
      "sessions": "Sessions",
      "emails": "Emails",
      "consent": "Consent",
      "feedback": "Feedback"
    },
    "audit": {
      "at": "Time",
      "actor": "Actor",
      "action": "Action",
      "result": "Result",
      "brev": "Brev",
      "detail": "Detail",
      "empty": "No actions recorded yet."
    },
    "segments": {
      "start": "Started",
      "stop": "Stopped",
      "duration": "Duration",
      "rate": "Rate",
      "cost": "Cost",
      "by": "Started / stopped by",
      "total": "Total billed GPU time",
      "running": "running",
      "empty": "The GPU has not run yet.",
      "candidate": "Candidate GPU usage",
      "admin": "Preparation / startup / waiting (excluded from allowance)",
      "category": "Usage category",
      "startup": "the workspace startup / unavailable (excluded from allowance)",
      "metered": "Brev metered this month",
      "meteredBreakdown": "GPU {{compute}} · disk {{storage}} · billed {{rate}}/h",
      "meteredNoRate": "GPU {{compute}} · disk {{storage}}"
    },
    "sessions": {
      "created": "Created",
      "lastSeen": "Last seen",
      "expires": "Expires",
      "ip": "IP",
      "agent": "Browser",
      "revoke": "Revoke",
      "revoked": "Revoked",
      "empty": "No sessions."
    },
    "emails": {
      "at": "Sent",
      "to": "To",
      "template": "Template",
      "subject": "Subject",
      "status": "Status",
      "empty": "No emails sent yet.",
      "statuses": {
        "queued": "Queued",
        "sent": "Sent",
        "failed": "Failed",
        "logged": "Logged only",
        "suppressed": "Disabled in Settings"
      }
    },
    "consent": {
      "version": "Agreement version",
      "accepted": "Accepted",
      "ip": "IP",
      "agent": "Browser",
      "empty": "The candidate has not accepted the privacy agreement yet."
    },
    "candidate": {
      "title": "Candidate",
      "notes": "Notes",
      "edit": "Edit",
      "editTitle": "Edit assignment",
      "saved": "Saved"
    },
    "spend": {
      "title": "Spend",
      "computeOf": "GPU {{compute}} of {{budget}} soft budget",
      "rate": "{{rate}}/h",
      "diskEstimated": "disk estimated until the next Brev sync",
      "diskDetail": "{{stopped}} stopped · {{amount}}",
      "legend": {
        "candidate": "Candidate GPU time",
        "admin": "Preparation / admin GPU time",
        "disk": "Disk (whole life, running or stopped)"
      }
    },
    "reopen": {
      "body": "Set a fresh remaining GPU allowance and a new window starting now. Previous usage and files are kept. The machine must be stopped. Share the new invite link with the candidate; old links and sign-ins will no longer work. No email is sent automatically.",
      "hours": "New remaining GPU hours",
      "days": "New window (days from now)",
      "success": "Assignment reopened. Copy and share the new invite link."
    },
    "feedback": {
      "hint": "Optional feedback about the assessment process. Removed with assignment data after the 90-day retention period.",
      "empty": "No feedback submitted.",
      "loading": "Loading feedback…",
      "error": "Could not load feedback.",
      "retry": "Try again"
    },
    "limits": {
      "confirmExpiry": "Allow this change to end access immediately if a limit is already past."
    },
    "reviewTitle": "Review and activity"
  },
  "instances": {
    "title": "All Brev instances",
    "hint": "Everything in the Brev organisation, including workspaces the assessment portal does not manage.",
    "fetchedAt": "Fetched <timestamp/>",
    "managed": "Managed",
    "unmanaged": "Unmanaged",
    "cols": {
      "name": "Name",
      "status": "Status",
      "type": "Type",
      "ip": "IP",
      "created": "Created",
      "candidate": "Candidate",
      "metered": "Brev metered (month)"
    },
    "stop": "Stop",
    "stopAll": "Stop all running",
    "stopAllTitle": "Stop every running instance?",
    "stopAllBody": "{{count}} running workspace(s) will be stopped, including unmanaged ones. Candidates lose their running session (files are kept). Type STOP to confirm.",
    "stopAllConfirm": "STOP",
    "stopped": "Stopped {{count}} instance(s)",
    "stopOne": "Stop “{{name}}”?",
    "empty": "No workspaces in the Brev organisation.",
    "refresh": "Refresh",
    "meteredSummary": "Brev metered month to date: {{total}}",
    "meteredSyncedAt": "synced <timestamp/>",
    "meteredNever": "Brev metered usage has not been synced yet. It syncs every hour; use Sync usage to fetch it now.",
    "syncUsage": "Sync usage",
    "synced": "Synced Brev usage: {{count}} workspace(s), {{total}} this month",
    "syncFailed": "Brev usage could not be fetched",
    "meteredCompute": "GPU {{amount}}",
    "meteredStorage": "disk {{amount}}/day",
    "meteredRate": "billed {{rate}}/h"
  },
  "settings": {
    "workspace": {
      "title": "Secure workspace access",
      "hint": "When enabled, new workspaces receive secure access automatically. Existing workspaces require explicit setup; turning this off affects new setups only.",
      "enabled": "Automatically prepare secure access for new workspaces"
    },
    "autoPause": {
      "title": "GPU auto-pause",
      "enabled": "Auto-pause after manual admin start",
      "hint": "When disabled, manual admin starts stay running during preparation until paused or the candidate launches. Initial automatic preheat still pauses after Isaac Sim stays ready for 3 minutes. Candidate GPU time and deadline rules apply after launch."
    },
    "title": "Settings",
    "readOnly": "You can change your own email language. System settings are managed by the owner.",
    "saved": "Settings saved",
    "admins": {
      "title": "Admins",
      "hint": "Admins sign in through Cloudflare Access and must be listed here.",
      "email": "Email",
      "name": "Name",
      "add": "Add admin",
      "remove": "Remove",
      "removeTitle": "Remove {{email}}?",
      "removeBody": "They lose access to the admin area immediately.",
      "addedBy": "added by {{by}}",
      "ownerLocked": "The owner cannot be removed.",
      "emailLanguage": "Email language"
    },
    "rates": {
      "title": "Fallback GPU estimates (US$/hour)",
      "hint": "New workspaces automatically use Brev’s machine and region catalog estimate. These fallback values apply only when a quote is unavailable. Existing workspace rate snapshots stay unchanged. Brev metered usage remains the actual cost."
    },
    "limits": {
      "title": "Limits",
      "otpAttempts": "OTP attempt limit",
      "otpAttemptsHint": "Incorrect codes allowed per invite (1–20). Reaching this limit disables the invite until HR generates a new link. Resending a code does not reset the count.",
      "maxActive": "Max active assignments",
      "costBudget": "Soft cost budget per candidate (US$)",
      "costHint": "Alerts at 80 % and 100 %. Nothing is blocked."
    },
    "defaults": {
      "title": "Defaults for new assignments",
      "gpu": "GPU",
      "disk": "Disk (GB)",
      "budget": "GPU budget (minutes)",
      "window": "Window (days)",
      "invite": "Invite validity (days)",
      "resume": "Resume estimate shown to candidates (min, max minutes)",
      "gpuVscodeDisk": "GPU + VS Code disk (GB)",
      "continuousSession": "Enable continuous-session limits",
      "continuousSessionHint": "Allow continuous-session limits on new assignments. Existing assignments keep their current limits.",
      "diskHint": "Default disk size for Isaac Sim and GPU + VS Code assignments."
    },
    "agreement": {
      "title": "Privacy agreement",
      "version": "Agreement version",
      "warning": "Changing the version forces every candidate to accept the agreement again on their next visit. Update docs/privacy-agreement.md first."
    },
    "alerts": {
      "title": "Alert recipients",
      "hint": "Admin emails (spend, failures, credential, digest) go here.",
      "add": "Add email",
      "placeholder": "name@example.com"
    },
    "tests": {
      "title": "Connection tests",
      "brev": "Test Brev connection",
      "email": "Send test email",
      "emailTo": "Send to",
      "ok": "OK",
      "failed": "Failed",
      "latency": "{{ms}} ms"
    },
    "danger": {
      "title": "Danger zone",
      "body": "Deleting instances and revoking assignments happens on each assignment page with a typed confirmation. “Stop all running” lives under All Brev instances."
    },
    "notifications": {
      "candidate": "Candidate emails",
      "admin": "Admin emails",
      "candidateHint": "Lifecycle emails start after the candidate’s first sign-in. Preheat completion notifies admins only. Ready emails also respect the candidate’s “Notify me” preference.",
      "adminHint": "Sent to the alert recipients below, or the owner when the list is empty.",
      "hint": "Save to apply to future events. Turning a switch back on does not resend skipped emails or change timers and machine actions.",
      "signinRequired": "Sign-in codes are always enabled so candidates can log in.",
      "templates": {
        "instance_ready": "Isaac Sim ready after warm-up",
        "budget_5h": "GPU time remaining: 5 hours",
        "budget_3h": "GPU time remaining: 3 hours",
        "budget_1h": "GPU time remaining: 1 hour",
        "budget_0h": "GPU allowance exhausted",
        "window_5h": "Assignment window remaining: 5 hours",
        "window_3h": "Assignment window remaining: 3 hours",
        "window_1h": "Assignment window remaining: 1 hour",
        "session_closed": "Session closed",
        "extended": "GPU allowance or assignment window extended",
        "admin_first_launch": "Candidate’s first launch",
        "admin_instance_ready": "Isaac Sim warm-up complete",
        "admin_finished": "Candidate finished",
        "admin_instance_failure": "Instance failure",
        "admin_spend": "Spending threshold reached",
        "admin_credential_failure": "Brev credential health failure",
        "admin_digest": "Daily summary",
        "admin_brev_login_required": "Brev login required"
      }
    }
  },
  "workspaceFailure": {
    "activationFailed": "Secure access could not be activated. After resolving the issue, select Retry verification to check the connection and activate access.",
    "certificatePending": "The secure connection certificate is not ready yet. Wait a few minutes, then select Retry verification.",
    "certificateVerificationFailed": "The secure connection certificate could not be verified. This may be temporary; wait a few minutes, then select Retry verification.",
    "connectorNotReady": "The workspace connection is not ready. Allow setup to finish and make sure the host is running, then select Retry verification.",
    "setupFailed": "Secure access setup could not finish. Resolve the issue shown by the diagnostic code, then retry setup.",
    "retryVerify": "Retry verification",
    "diagnostic": "Diagnostic code:"
  },
  "preview": {
    "title": "Admin preview",
    "hint": "Current secure-access flow with sample assignment recipients and independent email languages. Preview actions do not change real assignments.",
    "dashboard": "Dashboard",
    "assignment": "Assignment",
    "state": "Machine state",
    "localAction": "Preview: {{action}}",
    "aiPrompt": "How can I validate a grasp planner under partial occlusion?",
    "aiReply": "Use **synthetic occlusion cases**, measure grasp success and collisions, then compare against a clear-view baseline."
  },
  "misc": {
    "hoursOf": "{{used}} / {{budget}} h",
    "na": "—",
    "usd": "US${{n}}",
    "confirmTyped": "Type “{{word}}” to confirm",
    "unknownError": "Something went wrong."
  },
  "notifications": {
    "title": "Assignment notifications",
    "description": "Choose who receives alerts for this assignment. System alerts use the global recipient list.",
    "edit": "Edit recipients",
    "recipients": "Notification recipients",
    "recipientsHelp": "Enter one email per line, or separate emails with commas. All recipients use the admin email language.",
    "candidateEmailLocale": "Candidate email language",
    "adminEmailLocale": "Admin email language",
    "defaultRecipient": "You will receive this assignment’s notifications by default. You or the system owner can add recipients after creation.",
    "saved": "Notification settings saved",
    "englishDefault": "English (default)",
    "perPerson": "Each registered admin receives email in their account’s preferred language. Recipients without an account receive English.",
    "manageLanguages": "Manage personal email languages",
    "ownerOnly": "Only the assignment creator or system owner can edit recipients.",
    "editCandidateLanguage": "Edit email language",
    "owner": "Owner"
  },
  "emailPreview": {
    "title": "Email designs",
    "hint": "Preview every live template with sample data. Nothing is sent. Candidate language follows the assignment; admin language follows each recipient’s account, with English for unregistered recipients.",
    "guide": "Styleguide",
    "source": "Preview source",
    "designs": "Template designs",
    "log": "Delivery log",
    "template": "Email template",
    "candidate": "Candidate",
    "admin": "Admin",
    "signin": "Sign-in code",
    "language": "Email language",
    "width": "Preview width",
    "desktop": "Desktop",
    "mobile": "Mobile · 320 px",
    "format": "Message format",
    "html": "Email layout",
    "text": "Plain text",
    "error": "Email previews are unavailable. Please reload or check your admin access.",
    "subject": "Subject",
    "frame": "Email preview",
    "empty": "No deliveries recorded. Use Template designs to review emails without sending one."
  },
  "answer": {
    "title": "Answer",
    "loading": "Loading answer…",
    "error": "Could not load the answer.",
    "attempt": "Attempt / snapshot",
    "current": "Current attempt {{attempt}} · revision {{revision}}",
    "submission": "Submitted attempt {{attempt}} · {{time}}",
    "copy": "Copy Markdown",
    "export": "Export .md",
    "revisionHint": "The revision number counts saves; previous autosaved drafts are not retained. Submitted attempts are immutable snapshots.",
    "empty": "No answer has been saved.",
    "question": "Question · version {{version}}",
    "editQuestion": "Edit question",
    "questionMarkdown": "Question (Markdown)",
    "questionHelp": "Write the assignment question. Images and embedded HTML are not displayed. Maximum 200 KB.",
    "force": "I understand the candidate has opened this question. Publish a new question version and require the candidate to reconcile their draft.",
    "editError": "Question could not be updated. Check whether a forced update is required.",
    "notOpened": "Not opened yet",
    "reopen": "Start a new attempt and window now. Previous submissions remain read only. Old invitations and sessions stop working.",
    "questionStep": "Question",
    "windowDays": "Window (days from first open)",
    "sessionHelp": "Starts when this attempt opens. Leave blank to disable.",
    "createHint": "Create saves the question and issues an invitation. No machine is allocated; the clock starts when the candidate opens the assessment after consent.",
    "create": "Create assessment",
    "inviteText": "Your written assessment: {{url}}\nInvitation valid for {{inviteDays}} days. You have {{windowDays}} days from first opening, subject to any earlier session or fixed deadline. Read the question, write your Markdown answer and submit in the browser. No GPU is needed.",
    "snapshotQuestionUnavailable": "The question snapshot is unavailable for this historical submission.",
    "created": "Assessment created. Share the invitation when ready."
  },
  "ai": {
    "title": "AI chat",
    "liveUnavailable": "Live AI is disabled for this environment or its server credential is missing.",
    "enabled": "Enable AI for this assignment",
    "models": "Available models",
    "noModels": "No eligible catalog models are available. Refresh the catalog in Settings.",
    "defaultModel": "Default model",
    "limit": "Initial AI budget (USD)",
    "policy": "Assistant guidance",
    "policyHint": "Guidance shapes replies; it does not guarantee behavior. Tools and automatic answer sharing remain disabled.",
    "toolsOff": "Built-in chat does not run tools or share answers automatically. OpenCode supports workspace tools and Exa web search. Provider credentials stay on the server.",
    "retryCatalog": "Retry model catalog",
    "loading": "Loading AI details…",
    "error": "The AI operation failed. Refresh the details and try again.",
    "browser": "Conversations",
    "requests": "Requests",
    "config": "Usage and configuration",
    "captureNotice": "Review access and exports are audited. Answer drafts are not automatically shared with AI.",
    "conversation": "Conversation",
    "attempt": "Attempt {{n}}",
    "more": "Load more",
    "older": "Load older messages",
    "empty": "No recorded conversations.",
    "exportJson": "Export review page (JSON)",
    "exportMarkdown": "Export review page (Markdown)",
    "exportHint": "Exports contain the latest conversation page and latest message page per conversation. Use pagination above to review older records; JSON includes continuation cursors.",
    "partial": "OpenCode prompt recording is currently off. Request metadata covers only the managed proxy; personal keys and other endpoints are outside this record.",
    "requestUsage": "Input {{input}} · Output {{output}} · {{cost}}",
    "save": "Save AI configuration",
    "additional": "Add budget (USD)",
    "addBudget": "Add AI budget",
    "budgetAdded": "AI budget updated.",
    "globalTitle": "AI defaults",
    "provider": "AI provider",
    "providerOff": "Disabled",
    "refresh": "Refresh model catalog",
    "stale": "Catalog may be out of date. Refresh before selecting models.",
    "initial": "AI assistance",
    "off": "Disabled by default",
    "liveEnabled": "Live AI is enabled in testing.",
    "mockEnabled": "Local mock AI is enabled. Responses and usage are simulated.",
    "allZdrModels": "Candidates may choose any compatible text-chat model in OpenRouter’s current ZDR catalog. Availability and pricing vary by provider.",
    "requireByok": "Require BYOK",
    "requireByokHint": "Off: configured BYOK or OpenRouter credits may be used. On: only verified BYOK-only provider routes are allowed; other models fail closed. This does not change your OpenRouter account settings.",
    "catalogUnavailable": "The model catalog could not be refreshed. Your saved default is unchanged. Try refreshing the catalog again.",
    "summary": {
      "title": "AI usage",
      "enabled": "Enabled for assignment",
      "disabled": "Disabled for assignment",
      "pending": "Accounting pending",
      "used": "Used",
      "budget": "Budget",
      "remaining": "Remaining",
      "unknown": "Pending",
      "lessThan": "< {{amount}}",
      "progress": "AI budget usage",
      "progressValue": "{{used}} used of {{budget}}",
      "pendingHint": "Showing latest known usage. Provider billing is still being reconciled.",
      "providerUpdated": "Provider checked <timestamp/>",
      "stale": "Usage could not refresh. Showing the last available values.",
      "mock": "Simulated usage in this environment.",
      "unavailable": "AI is configured, but unavailable in this environment.",
      "providerRecorded": "Provider-recorded usage",
      "lastActivity": "Latest request activity",
      "noActivity": "No recorded activity"
    },
    "recordWorkspaceChat": "Record managed OpenCode conversations",
    "recordingHelp": "Save candidate prompts and visible replies for authorized assessment reviewers under the existing retention policy. The candidate must accept the recording notice before using managed OpenCode.",
    "recordingLocked": "Recording was not enabled before this attempt started. It cannot be enabled for an attempt already in progress.",
    "sources": {
      "browser": "Built-in chat",
      "opencode": "OpenCode"
    },
    "workspaceCaptureScope": "Partial record of candidate prompts and visible replies sent through managed OpenCode. Internal reasoning, tool outputs and requests using personal keys or other endpoints are excluded.",
    "partialRecorded": "Managed OpenCode prompt recording is enabled. Recorded prompts and visible replies appear in Conversations; request metadata is listed here. Coverage is partial and excludes personal keys and other endpoints."
  }
}
